import { CARD } from "./cardGeometry";
import type { InputCluster, PlacedBox } from "./graphLayout";
import { SKIP_CHANNELS } from "./skipChannels";
import { isExpandable, type GraphNode } from "./workflowGraph";

/**
 * The vertical layout of one level (F59): for phones, narrow windows and the split view. The
 * pipeline is one column on the left, top to bottom, its cards aligned on the left edge; the
 * edges between its steps run in channels beside it (`edgeRoutes`). Each cluster of inputs
 * is a column of its own, in the order its lines run, to the right of the pipeline, its
 * bottom just above the first step it feeds; so every input's line leaves it with nothing
 * beside it, and drops into its step's tab from the gap above. Clusters stack
 * downwards; when one is taller than the room above its step, the step moves down. Inputs
 * that lead to no step come last, under the others. Pure: it only places the boxes.
 */

export const COLUMN_LAYOUT = {
  /** Between a cluster's bottom and the top of the step it feeds: room for the line to curve into the tab. */
  feedRise: 48,
  /** Between two steps, at least: a feeding line curves in the gap above a step. */
  stepGap: 56,
  /** Between the channels right of the column and the inputs. */
  sideGap: 32,
  /** Between two clusters. */
  clusterGap: 24,
} as const;

/** How many channels a column needs on each side: one for the edges to the next step, one per lane of a skipping edge. */
function columnChannels(lanes: number): number {
  return 1 + lanes;
}

/** How far channels reach from the column's side: their margin and spacing (`SKIP_CHANNELS`). */
export function channelsDepth(lanes: number): number {
  return SKIP_CHANNELS.runMargin + columnChannels(lanes) * SKIP_CHANNELS.runSpacing;
}

interface ColumnOptions {
  /** The gap after a pipeline card, before the next: room for the edges that cross it. */
  readonly gapAfter: (above: string, below: string) => number;
  /** How many edges skip steps at this level. */
  readonly lanes: number;
  /** The level's feeds: an input card and the pipeline step it feeds. */
  readonly feeds: readonly Feed[];
}

/** An input card at a level and the pipeline step it feeds there: one line, however many ports. */
export type Feed = readonly [input: string, step: string];

/** How far an input's line drops below the input before it turns. */
export const FEED_DROP = 12;

interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** A feed's line beside the column: its lane, from just below its input down to where it crosses over its step. */
export interface FeedLine {
  readonly top: number;
  readonly lane: number;
  readonly crossing: number;
}

/** A feed's key, the same in the layout and the routes. */
export function feedKey([input, step]: Feed): string {
  return `${input}\n${step}`;
}

/**
 * Each feed's own lane and crossing (Johannes, 4 Nov 23:42): its lane right of every pipeline
 * card beside its line and of every lane placed before it there, its crossing over its step one
 * channel higher than the feed into the same step before it, so no two lines share a run.
 * Pure: the layout places the input cards by them, and the routes draw them.
 */
export function feedLines(feeds: readonly Feed[], rectOf: (id: string) => Rect, column: readonly Rect[]): ReadonlyMap<string, FeedLine> {
  const { runMargin, runSpacing } = SKIP_CHANNELS;
  const spans = feeds
    .map((feed) => {
      const input = rectOf(feed[0]);
      const step = rectOf(feed[1]);
      return { feed, top: input.y + input.height + FEED_DROP, bottom: step.y, floor: step.x + step.width };
    })
    .sort((one, other) => one.top - other.top || feedKey(one.feed).localeCompare(feedKey(other.feed)));
  const lines = new Map<string, FeedLine>();
  const placedLanes: { top: number; bottom: number; lane: number }[] = [];
  const intoStep = new Map<string, number>();
  for (const span of spans) {
    const beside = (top: number, bottom: number) => top < span.bottom && span.top < bottom;
    const cards = column.filter((card) => beside(card.y, card.y + card.height)).map((card) => card.x + card.width);
    const lanes = placedLanes.filter((other) => beside(other.top, other.bottom)).map((other) => other.lane + runSpacing);
    const lane = Math.max(span.floor + runMargin, ...cards.map((right) => right + runMargin), ...lanes);
    const index = intoStep.get(span.feed[1]) ?? 0;
    intoStep.set(span.feed[1], index + 1);
    placedLanes.push({ top: span.top, bottom: span.bottom, lane });
    lines.set(feedKey(span.feed), { top: span.top, lane, crossing: span.bottom - CARD.tab.rise - runMargin - index * runSpacing });
  }
  return lines;
}

/** Places the level's boxes; returns where the channels right of the column end. */
export function placeColumn(main: readonly GraphNode[], clusters: readonly InputCluster[], placed: ReadonlyMap<string, PlacedBox>, options: ColumnOptions): { readonly channelsRight: number } {
  const boxOf = (id: string): PlacedBox => {
    const box = placed.get(id);
    if (box === undefined) throw new Error(`column layout: no node ${id}`);
    return box;
  };
  const columnLeft = channelsDepth(options.lanes);
  const columnRight = columnLeft + Math.max(0, ...main.map((node) => boxOf(node.id).width));
  const channelsRight = columnRight + channelsDepth(options.lanes);
  const stacked = clusters.map((cluster) => stack(cluster, boxOf));
  const byAnchor = new Map(stacked.flatMap((cluster) => (cluster.anchor === null ? [] : [[cluster.anchor, cluster] as const])));
  // Each feed into a step crosses over it one channel higher than the one before: the gap above it holds them all.
  const feedRoom = (step: string) => options.feeds.filter(([, fed]) => fed === step).length * SKIP_CHANNELS.runSpacing;
  const place = (cluster: InputCluster, top: number) => {
    for (const id of cluster.members) {
      boxOf(id).y += top;
    }
  };

  let cursor = 0;
  // The lowest cluster's bottom so far: the next one starts under it.
  let clustersBottom = Number.NEGATIVE_INFINITY;
  main.forEach((node, index) => {
    const box = boxOf(node.id);
    const cluster = byAnchor.get(node.id);
    let top = cursor;
    if (cluster !== undefined) {
      const rise = COLUMN_LAYOUT.feedRise + feedRoom(node.id);
      top = Math.max(cursor, clustersBottom + COLUMN_LAYOUT.clusterGap + cluster.size.height + rise);
      clustersBottom = top - rise;
      place(cluster, clustersBottom - cluster.size.height);
    }
    box.x = columnLeft;
    box.y = top;
    const next = main[index + 1];
    cursor = top + box.height + (next === undefined ? 0 : Math.max(COLUMN_LAYOUT.stepGap, options.gapAfter(node.id, next.id)) + feedRoom(next.id));
  });
  for (const cluster of stacked.filter((candidate) => candidate.anchor === null || !main.some((node) => node.id === candidate.anchor))) {
    const top = Number.isFinite(clustersBottom) ? clustersBottom + COLUMN_LAYOUT.clusterGap : 0;
    place(cluster, top);
    clustersBottom = top + cluster.size.height;
  }
  const column = main.map((node) => boxOf(node.id));
  // A line leaving an open frame runs down inside its width to a later step: input cards stay clear of it below the frame too (Sol r1 S1).
  const levelBottom = Math.max(0, ...column.map((box) => box.y + box.height));
  const clear = main.map((node, index) => {
    const box = column[index] ?? boxOf(node.id);
    return isExpandable(node) && node.expanded ? { ...box, height: levelBottom - box.y } : box;
  });
  placeBeside(stacked, column, clear, options.feeds, boxOf);
  return { channelsRight };
}

/**
 * Moves each cluster beside the column, only as far right as the pipeline cards and the feed
 * lanes beside it need (Johannes, 4 Nov 23:43: "does not position all those cards to far out"),
 * so the vertical layout stays narrow; a lane longer than its cluster may run right of it.
 */
function placeBeside(clusters: readonly InputCluster[], column: readonly PlacedBox[], clear: readonly Rect[], feeds: readonly Feed[], boxOf: (id: string) => PlacedBox): void {
  // The lanes read the column as the routes do; the cards keep clear of `clear`, the column with each open frame's exits.
  const lines = [...feedLines(feeds, boxOf, column).values()];
  for (const cluster of clusters) {
    const boxes = cluster.members.map((id) => boxOf(id));
    const top = Math.min(...boxes.map((box) => box.y));
    const bottom = Math.max(...boxes.map((box) => box.y + box.height));
    const beside = (otherTop: number, otherBottom: number) => otherTop < bottom && top < otherBottom;
    const cards = clear.filter((card) => beside(card.y, card.y + card.height)).map((card) => card.x + card.width + COLUMN_LAYOUT.sideGap);
    const lanes = lines.filter((line) => beside(line.top, line.crossing)).map((line) => line.lane + COLUMN_LAYOUT.sideGap / 2);
    const left = Math.max(COLUMN_LAYOUT.sideGap, ...cards, ...lanes);
    for (const box of boxes) {
      box.x += left;
    }
  }
}

/**
 * A cluster as one column: its members top to bottom in the order of the cluster's own layout
 * (its ranks run downwards), each its line's drop below the one above.
 */
function stack(cluster: InputCluster, boxOf: (id: string) => PlacedBox): InputCluster {
  const boxes = cluster.members.map((id) => boxOf(id)).sort((upper, lower) => upper.y - lower.y || upper.x - lower.x);
  let top = 0;
  for (const box of boxes) {
    box.x = 0;
    box.y = top;
    top += box.height + COLUMN_LAYOUT.feedRise;
  }
  return { ...cluster, size: { width: Math.max(0, ...boxes.map((box) => box.width)), height: Math.max(0, top - COLUMN_LAYOUT.feedRise) } };
}
